import { Row, Col, Form, Input, Button, Divider, Typography, Result, message } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { Link, useNavigate } from 'react-router-dom';
import { RouteNames } from '../../routes/routeName';
import { HttpStatus } from '../../constant/responseStatus';
import { AccountService } from '../../services/accountService';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import MessageModal from 'src/components/modal/messageModal';
import { PageDocument, StringConstants } from 'src/constant/stringConstants';
import { RegexPattern } from 'src/constant/constants';
import TranslationKey from 'src/i18n/translation';
import { Loading } from 'src/components/loaging/Loading';
import { useLocation } from 'react-router-dom';
import { ResetPasswordResponse } from 'src/constant/apiResponseConstants';
const { Title, Text } = Typography;
const year = new Date().getFullYear();

export default function ResetPassword() {
  const logo = require('../../assets/images/logo/white@3x.png');
  const bannerImg = require('../../assets/images/banner/Image.png');
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [childModal, setChildModal] = useState<JSX.Element>(<></>);
  const handleCloseModal = () => setIsOpenModal(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.title = `${TranslationKey('resetPassword.title')} - ${PageDocument.Title}`;
  }, []);

  const onResetPassword = async () => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');

    if (!token) {
      return;
    }

    setLoading(true);

    try {
      const newPassword = form.getFieldValue('password');

      const response =
        await new AccountService()
          .resetPasswordAsync(token, newPassword);

      setTimeout(() => {
        setLoading(false);
      }, 1000);

      if (response.status !== HttpStatus.OK) {
        const isKnownError =
          Object.values(ResetPasswordResponse)
            .includes(response.data as ResetPasswordResponse);

        const errorKey = isKnownError
          ? `resetPassword.fail.${response.data}`
          : 'resetPassword.alert.fail';

        errorPopup(
          TranslationKey(errorKey),
          <Button size='large'
            type='primary'
            shape='round'
            className='btn-primary'
            onClick={handleCloseModal}>
            {TranslationKey('button.ok')}
          </Button>
        );

        return;
      }

      successPopup(
        TranslationKey('resetPassword.alert.success'),
        <Button
          size='large'
          type='primary'
          shape='round'
          className='btn-primary'
          onClick={() => navigate(RouteNames.login)}>
          {TranslationKey('forgotPassword.backToLogin')}
        </Button>
      );
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const errorPopup = (
    msg: string,
    extra: JSX.Element) => {
    setChildModal(
      <Result status='error'
        title={msg} className='result-message'
        extra={extra} />);
    setIsOpenModal(true);
    setLoading(false);
  };

  const successPopup = (
    msg: string,
    extra: JSX.Element) => {
    setChildModal(<Result
      status='success'
      title={msg}
      className='result-message'
      extra={extra} />);
    setIsOpenModal(true);
    setLoading(false);
  };

  return (
    <>
      <Loading isLoading={loading} />
      <Content className='login'>
        <Row>
          <Col xs={0} xl={12}>
            <Link to={RouteNames.home}>
              <img src={logo}
                alt='alive united plus'
                className='img-logo' />
            </Link>
            <p className='copy-right'>All contents &copy; copyright {year} Alive United {process.env.REACT_APP_VERSION}</p>
            <img src={bannerImg} alt='alive banner' className='img' />
          </Col>
          <Col xs={24} xl={12} className='flex-center-items'>
            <Form
              form={form}
              name='resetPasswordForm'
              layout='vertical'
              onFinish={onResetPassword}
              className='form-input'
              requiredMark={false}
            >
              <Title level={2} className='text-light'>
                {TranslationKey('resetPassword.title')}
              </Title>
              <Text className='text-gray'>
                {TranslationKey('resetPassword.titleDescription')}
              </Text>

              <Form.Item
                name='password'
                className='text-light mt-4'
                label={TranslationKey('resetPassword.password')}
                rules={[
                  {
                    required: true,
                    message: TranslationKey('resetPassword.passwordInvalidRequired'),
                  },
                  {
                    pattern: RegexPattern.password,
                    message: TranslationKey('resetPassword.passwordInvalidRule'),
                  },
                ]}
              >
                <Input.Password
                  placeholder={TranslationKey('resetPassword.password')}
                  className='input-radius-dark w-100'
                  size='large'
                  iconRender={(visible) =>
                    visible
                      ? <EyeTwoTone style={{ color: '#FFF' }} />
                      : <EyeInvisibleOutlined style={{ color: '#FFF' }} />
                  }
                />
              </Form.Item>
              <Form.Item
                name='confirmPassword'
                className='text-light'
                label={TranslationKey('resetPassword.confirmPassword')}
                rules={[
                  {
                    required: true,
                    message: TranslationKey('resetPassword.confirmPasswordInvalidRequired'),
                  },
                  {
                    pattern: RegexPattern.password,
                    message: TranslationKey('resetPassword.confirmPasswordInvalidRule'),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(TranslationKey('resetPassword.confirmPasswordNotMatch'))
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder={TranslationKey('resetPassword.confirmPassword')}
                  className='input-radius-dark w-100'
                  size='large'
                  iconRender={(visible) =>
                    visible
                      ? <EyeTwoTone style={{ color: '#FFF' }} />
                      : <EyeInvisibleOutlined style={{ color: '#FFF' }} />
                  }
                />
              </Form.Item>
              <Row className='mt-5' justify='center'>
                <Button
                  shape='round'
                  type='primary'
                  className='btn btn-primary w-100'
                  htmlType='submit'
                  size='large'
                  loading={loading}
                >
                  {TranslationKey('resetPassword.submit')}
                </Button>
              </Row>
            </Form>
          </Col>
        </Row>
      </Content>
      <MessageModal
        isModalOpen={isOpenModal}
        onClose={handleCloseModal}
        children={childModal} />
    </>
  );
}
